import { useFixingIndices } from '@src/providers/FixingIndexProvider';
import type { FixingIndex, FixingIndexMarkMessageData } from '@talos/kyoko';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';

/**
 * USD-pegged assets that can be normalized to USD for rate source lookups.
 * These assets (USD, USDT, USDC) are treated as equivalent for rate source purposes.
 * - TODO: replace with stable coin peg service (https://talostrading.atlassian.net/browse/PORT-17331) tokens
 */
export const USD_PEGGED_ASSETS = ['USD', 'USDT', 'USDC'] as const;

/**
 * Normalizes USD-pegged assets (USDT, USDC) to USD for rate source lookups.
 * USD itself is returned as-is.
 *
 * @param asset - The asset to normalize (e.g., 'USD', 'USDT', 'USDC', 'BTC')
 * @returns 'USD' if asset is USD-pegged, otherwise returns the asset unchanged
 */
export function normalizeUsdPeggedAsset(asset: string): string {
  return (USD_PEGGED_ASSETS as readonly string[]).includes(asset) ? 'USD' : asset;
}

/**
 * Checks if an asset is USD-pegged (USD, USDT, or USDC).
 *
 * @param asset - The asset to check
 * @returns True if the asset is USD-pegged
 */
export function isUsdPeggedAsset(asset: string): boolean {
  return (USD_PEGGED_ASSETS as readonly string[]).includes(asset);
}

/**
 * USD_PEGGED_NO_CONVERSION_INDEX is a special Fixing Index Mapping object for USD-pegged assets (those in USD_PEGGED_ASSETS array).
 * These assets don't require price conversion between them, so no fixing index is needed.
 * This constant represents that special case where no conversion rate is used.
 */
export const USD_PEGGED_NO_CONVERSION_INDEX: FixingIndexMapping = {
  Name: 'internal:NONE',
  DisplayName: 'None',
  Mark: null,
};

/**
 * Get price source fixings by collateral asset from fixing index mapping.
 * Looks up symbol pairs in the format `${collateralAsset}-${quoteCurrency}`.
 * Also checks for standalone asset mappings (e.g., "USD" without a dash).
 * Reads from the feed - no hardcoded fallbacks.
 *
 * @param collateralAsset - The collateral asset (e.g., "BTC", "ETH", "USD", "USDT", "USDC")
 * @param fixingIndexMapping - The fixing index mapping map
 * @returns Array of fixing objects with Mark objects
 */
export function getPriceSourceFixingsByCollateralAssetFromMapping(
  collateralAsset: string,
  fixingIndexMapping: FixingIndexMappingMap
): FixingIndexMapping[] {
  if (fixingIndexMapping.size === 0) {
    return [];
  }

  const allSources = new Map<string, FixingIndexMapping>();

  // Helper to accumulate mappings into allSources
  const addMappings = (mappings: FixingIndexMapping[]) => {
    for (const mapping of mappings) {
      allSources.set(mapping.Name, mapping);
    }
  };

  // Check standalone asset (O(1) lookup)
  const standaloneMappings = fixingIndexMapping.get(collateralAsset);
  if (standaloneMappings) {
    addMappings(standaloneMappings);
  }

  // Iterate for prefix matches (unavoidable for prefix search)
  const prefix = `${collateralAsset}-`;
  for (const [key, mappings] of fixingIndexMapping) {
    if (key.startsWith(prefix)) {
      addMappings(mappings);
    }
  }

  return allSources.size > 0 ? Array.from(allSources.values()) : [];
}

/**
 * Get price source fixings by full symbol from fixing index mapping.
 * Takes two params representing the part before and after the dash.
 * Quote Asset lookups: If USD, USDT, or USDC is passed, returns fixings for all three variants uniquely.
 *
 * @param beforeDash - The part before the dash (should be Collateral Asset (Loan) or Underlier Asset (Derivs))
 * @param afterDash - The part after the dash (should be Borrowed Asset (Loan) or Quote Asset (Derivs))
 * @param fixingIndexMapping - The fixing index mapping map
 * @returns Array of fixing objects with Mark objects
 */
export function getPriceSourceFixingsByFullSymbolFromMapping(
  beforeDash: string,
  afterDash: string,
  fixingIndexMapping: FixingIndexMappingMap
): FixingIndexMapping[] {
  const isAfterDashUsdPegged = isUsdPeggedAsset(afterDash);
  const quoteAssets = isAfterDashUsdPegged ? (USD_PEGGED_ASSETS as readonly string[]) : [afterDash];

  const allSources = new Map<string, FixingIndexMapping>();

  // Helper to accumulate mappings into allSources
  const addMappings = (mappings: FixingIndexMapping[]) => {
    for (const mapping of mappings) {
      allSources.set(mapping.Name, mapping);
    }
  };

  // Check standalone USD mapping if both assets are USD-pegged
  if (isUsdPeggedAsset(beforeDash) && isAfterDashUsdPegged) {
    const standaloneMappings = fixingIndexMapping.get('USD');
    if (standaloneMappings) {
      addMappings(standaloneMappings);
    }
  }

  // Check symbol pairs for each quote asset
  for (const quote of quoteAssets) {
    const mappings = fixingIndexMapping.get(`${beforeDash}-${quote}`);
    if (mappings) {
      addMappings(mappings);
    }
  }

  return allSources.size > 0 ? Array.from(allSources.values()) : [];
}

/**
 * Hook to get price source fixings by full symbol.
 * Takes two params representing the part before and after the dash.
 * Quote Asset lookups: If USD, USDT, or USDC is passed, returns fixings for all three variants uniquely.
 *
 * @param beforeDash - The part before the dash (should be Collateral Asset (Loan) or Underlier Asset (Derivs))
 * @param afterDash - The part after the dash (should be Borrowed Asset (Loan) or Quote Asset (Derivs))
 * @returns Array of fixing objects with Mark objects
 */
export function usePriceSourceFixingsByFullSymbol(
  beforeDash: string | undefined,
  afterDash: string | undefined
): FixingIndexMapping[] {
  const fixingIndexMapping = useFixingIndexMapping();

  return useMemo(() => {
    if (!beforeDash || !afterDash) {
      return [];
    }

    return getPriceSourceFixingsByFullSymbolFromMapping(beforeDash, afterDash, fixingIndexMapping);
  }, [beforeDash, afterDash, fixingIndexMapping]);
}

/**
 * Hook to get rate source fixings for a borrowed asset.
 * Normalizes USD-pegged assets (USDT, USDC) to USD for lookup.
 *
 * @param borrowedAsset - The borrowed asset (e.g., 'USD', 'USDT', 'USDC')
 * @returns Array of rate source fixing options
 */
export function useRateSourceFixingsForAsset(borrowedAsset: string | undefined): RateSourceFixing[] {
  const rateSourceMapping = useRateSourceMapping();

  return useMemo(() => {
    if (!borrowedAsset) {
      return [];
    }

    const normalizedAsset = normalizeUsdPeggedAsset(borrowedAsset);
    const sources = rateSourceMapping.get(normalizedAsset);
    if (!sources) {
      return [];
    }

    return sources.map(source => ({
      index: source.Name,
      displayName: source.DisplayName ?? source.Name,
      description: source.Description,
    }));
  }, [borrowedAsset, rateSourceMapping]);
}

/**
 * Hook to get all available collateral assets.
 * Extracts unique collateral assets from the fixing index mapping (price sources) and returns them as options with value and label.
 * Includes assets that exist in the price source mapping.
 * Also includes USD-pegged assets (USD, USDT, USDC) unconditionally,
 * since these can be used as collateral with USD_PEGGED_NO_CONVERSION_INDEX.
 *
 * @returns Array of collateral asset options sorted alphabetically
 */
export function useAvailableCollateralAssets(): Array<{ value: string; label: string }> {
  const fixingIndexMapping = useFixingIndexMapping();

  return useMemo(() => {
    const collateralAssets = new Set<string>();

    // Add assets from fixing index mapping (price sources)
    for (const key of fixingIndexMapping.keys()) {
      if (key.includes('-')) {
        const collateralAsset = key.split('-')[0];
        collateralAssets.add(collateralAsset);
      } else {
        // Handle standalone asset mappings (e.g., "USD" without a dash)
        collateralAssets.add(key);
      }
    }

    // Add USD-pegged assets unconditionally
    // These can be used as collateral with USD_PEGGED_NO_CONVERSION_INDEX
    for (const usdPeggedAsset of USD_PEGGED_ASSETS) {
      collateralAssets.add(usdPeggedAsset);
    }

    const assets = Array.from(collateralAssets).toSorted((a, b) => a.localeCompare(b));
    return assets.map(asset => ({
      value: asset,
      label: asset,
    }));
  }, [fixingIndexMapping]);
}

export type FixingIndexMapping = {
  Name: string;
  DisplayName?: string;
  Description?: string;
  Mark: FixingIndexMarkMessageData | null;
  NominalTime?: string;
  NominalIndexTimeZone?: string;
  Asset?: string;
  QuoteCurrency?: string;
};

export type RateSourceMapping = {
  Name: string;
  Asset: string;
  DisplayName?: string;
  Description?: string;
};

export type RateSourceMappingMap = Map<string, Array<RateSourceMapping>>;

export type RateSourceFixing = {
  index: string;
  displayName: string;
  description?: string;
};

/**
 * Map type for fixing index mappings
 */
export type FixingIndexMappingMap = Map<string, Array<FixingIndexMapping>>;

/**
 * Serializable record type for fixing index mappings (for Redux store)
 */
export type FixingIndexMappingRecord = Record<string, Array<FixingIndexMapping>>;

/**
 * Convert FixingIndexMappingMap to FixingIndexMappingRecord for Redux storage
 */
export function fixingIndexMappingMapToRecord(map: FixingIndexMappingMap): FixingIndexMappingRecord {
  const record: FixingIndexMappingRecord = {};
  map.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

/**
 * Convert FixingIndexMappingRecord back to FixingIndexMappingMap for use in strategy methods
 */
export function fixingIndexMappingRecordToMap(
  record: FixingIndexMappingRecord | undefined
): FixingIndexMappingMap | undefined {
  if (!record) {
    return undefined;
  }
  const map: FixingIndexMappingMap = new Map();
  Object.entries(record).forEach(([key, value]) => {
    map.set(key, value);
  });
  return map;
}

/**
 * Hook to map Asset to Name and DisplayName from fixing indices.
 * Returns a Map where each symbol (Asset-QuoteCurrency) maps to an array of { Name, DisplayName, Mark } objects
 * for all indices that reference that symbol.
 * Derived from fixingIndicesList and fixingIndexMarksByIndexID feeds.
 *
 * @returns FixingIndexMappingMap
 */
export function useFixingIndexMapping(): FixingIndexMappingMap {
  const { fixingIndicesList, fixingIndexMarksByIndexID } = useFixingIndices();

  return useMemo(() => {
    const mapping: FixingIndexMappingMap = new Map();

    if (!fixingIndicesList || !fixingIndexMarksByIndexID) {
      return mapping;
    }

    // Group indices by symbol (Asset-QuoteCurrency)
    const indicesBySymbol = new Map<string, FixingIndex[]>();

    for (const index of fixingIndicesList) {
      if (
        index.ValueType !== 'Price' ||
        !index.Asset ||
        !index.QuoteCurrency ||
        !fixingIndexMarksByIndexID.has(index.IndexID)
      ) {
        continue;
      }

      const symbol = `${index.Asset}-${index.QuoteCurrency}`;
      const existingIndices = indicesBySymbol.get(symbol);
      if (existingIndices) {
        existingIndices.push(index);
      } else {
        indicesBySymbol.set(symbol, [index]);
      }
    }

    // Build mapping array for each symbol
    for (const [symbol, indicesForSymbol] of indicesBySymbol) {
      const mappings: FixingIndexMapping[] = indicesForSymbol.map(index => {
        const mark = fixingIndexMarksByIndexID.get(index.IndexID);
        return {
          Name: index.Name,
          DisplayName: index.DisplayName ?? index.Name,
          Description: index.Description,
          Mark: mark ?? null,
          NominalTime: mark?.NominalTime,
          NominalIndexTimeZone: index.NominalIndexTimeZone,
          Asset: index.Asset,
          QuoteCurrency: index.QuoteCurrency,
        };
      });
      mapping.set(symbol, mappings);
    }

    return mapping;
  }, [fixingIndicesList, fixingIndexMarksByIndexID]);
}

export function useRateSourceMapping(): RateSourceMappingMap {
  const { fixingIndicesList } = useFixingIndices();

  return useMemo(() => {
    const mapping: RateSourceMappingMap = new Map();

    if (!fixingIndicesList) {
      return mapping;
    }

    // Group rate sources by Asset, deduplicating by Name
    const rateSourcesByAsset = new Map<string, Map<string, RateSourceMapping>>();

    for (const index of fixingIndicesList) {
      // Filter: must have ValueType === 'Rate' (required as of Backend 2.55+)
      if (index.ValueType !== 'Rate') {
        continue;
      }

      const asset = index.Asset ?? '';
      const rateSource: RateSourceMapping = {
        Name: index.Name,
        Asset: asset,
        DisplayName: index.DisplayName ?? index.Name,
        Description: index.Description,
      };

      let sourcesByName = rateSourcesByAsset.get(asset);
      if (!sourcesByName) {
        sourcesByName = new Map();
        rateSourcesByAsset.set(asset, sourcesByName);
      }
      // Deduplicate by Name - if same Name exists, keep the first one
      if (!sourcesByName.has(rateSource.Name)) {
        sourcesByName.set(rateSource.Name, rateSource);
      }
    }

    // Map each asset to its rate sources from the feed (convert Map to Array)
    for (const [asset, sourcesByName] of rateSourcesByAsset) {
      const sources = Array.from(sourcesByName.values());
      mapping.set(asset, sources);

      // For USD rate sources, also associate them with USDT and USDC
      // This is the only place we use USD_PEGGED_ASSETS to associate assets together
      if (asset === 'USD') {
        // Directly set USDT and USDC to use the same sources as USD
        mapping.set('USDT', sources);
        mapping.set('USDC', sources);
      }
    }

    return mapping;
  }, [fixingIndicesList]);
}

/**
 * Helper function to check if a borrowed asset has rate sources registered in the mapping.
 * Normalizes USD-pegged assets (USDT, USDC) to USD for lookup.
 *
 * @param borrowedAsset - The borrowed asset to check (e.g., 'USD', 'USDT', 'USDC', 'BTC')
 * @param rateSourceMapping - The rate source mapping map
 * @returns True if the asset has rate sources registered, false otherwise
 */
export function hasRateSourcesForAsset(
  borrowedAsset: string | undefined,
  rateSourceMapping: RateSourceMappingMap
): boolean {
  if (!borrowedAsset) {
    return false;
  }

  const normalizedAsset = normalizeUsdPeggedAsset(borrowedAsset);
  const rateSourcesForAsset = rateSourceMapping.get(normalizedAsset);

  return rateSourcesForAsset != null && rateSourcesForAsset.length > 0;
}

/**
 * Hook to check if a borrowed asset has rate sources registered.
 * Normalizes USD-pegged assets (USDT, USDC) to USD for lookup.
 *
 * @param borrowedAsset - The borrowed asset to check (e.g., 'USD', 'USDT', 'USDC', 'BTC')
 * @returns True if the asset has rate sources registered, false otherwise
 */
export function useHasRateSourcesForAsset(borrowedAsset: string | undefined): boolean {
  const rateSourceMapping = useRateSourceMapping();

  return useMemo(() => {
    return hasRateSourcesForAsset(borrowedAsset, rateSourceMapping);
  }, [borrowedAsset, rateSourceMapping]);
}

/**
 * Find a fixing index mapping entry by name from the mapping.
 * Searches through all mappings to find one with matching Name.
 *
 * @param indexName - The name of the fixing index (e.g., 'XBX', 'BRRNY', 'OIS')
 * @param fixingIndexMapping - The fixing index mapping map
 * @returns The matching FixingIndexMapping entry, or undefined if not found
 */
export function findFixingMappingByName(
  indexName: string | undefined,
  fixingIndexMapping: FixingIndexMappingMap
): FixingIndexMapping | undefined {
  if (!indexName) {
    return undefined;
  }

  for (const mappings of fixingIndexMapping.values()) {
    const found = mappings.find(m => m.Name === indexName);
    if (found) {
      return found;
    }
  }

  return undefined;
}

/**
 * Hook to get fixing mark data by index name.
 * Returns fixing value, asset, timestamp, and timezone from fixing index marks.
 * Uses the mapping to get Asset/QuoteCurrency if available, falling back to FixingIndex lookup.
 *
 * @param indexName - The name of the fixing index (e.g., 'XBX', 'BRRNY', 'OIS')
 * @returns Object with Value (number | null), Asset (string), Currency (string), FormattedTimeStamp (string | undefined), or undefined if not found
 */
export function useFixingMarkByName(
  indexName: string | undefined
): { Value: number | null; Asset: string; Currency: string; FormattedTimeStamp: string | undefined } | undefined {
  const { fixingIndicesByName, fixingIndexMarksByIndexID } = useFixingIndices();
  const fixingIndexMapping = useFixingIndexMapping();

  return useMemo(() => {
    if (!indexName || !fixingIndicesByName || !fixingIndexMarksByIndexID) {
      return undefined;
    }

    // Try to get Asset/QuoteCurrency from mapping first
    const mappingEntry = findFixingMappingByName(indexName, fixingIndexMapping);
    const asset = mappingEntry?.Asset;
    const quoteCurrency = mappingEntry?.QuoteCurrency;

    const index = fixingIndicesByName.get(indexName);
    if (!index) {
      return undefined;
    }

    const mark = fixingIndexMarksByIndexID.get(index.IndexID);
    if (!mark) {
      return undefined;
    }

    const formattedTimeStamp =
      mark.NominalTime && index.NominalIndexTimeZone
        ? formatInTimeZone(mark.NominalTime, index.NominalIndexTimeZone, 'yyyy-MM-dd HH:mm z')
        : undefined;

    return {
      Value: mark.Mark ? parseFloat(mark.Mark) : null,
      Asset: asset ?? index.Asset ?? '',
      Currency: quoteCurrency ?? index.QuoteCurrency ?? '',
      FormattedTimeStamp: formattedTimeStamp,
    };
  }, [indexName, fixingIndicesByName, fixingIndexMarksByIndexID, fixingIndexMapping]);
}
